import { Box, Stack, Text, useMantineColorScheme, useMantineTheme } from '@mantine/core';
import { forwardRef, ReactNode } from 'react';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface TimelineItem {
  title: string;
  description?: string;
  date?: string;
  icon?: ReactNode;
  color?: string;
  isCurrent?: boolean;
}

export interface TimelineProps {
  items: TimelineItem[];
  gradientLine?: boolean;
  gradientVariant?: GradientVariant;
  activeIndex?: number;
  bulletSize?: number;
  lineWidth?: number;
}

const Timeline = forwardRef<HTMLDivElement, TimelineProps>(
  (
    {
      items,
      gradientLine = false,
      gradientVariant = 'primary',
      activeIndex,
      bulletSize = 12,
      lineWidth = 2,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const locale = usePreferencesStore((state) => state.locale);
    const timezone = usePreferencesStore((state) => state.timezone);
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);
    const isDark = colorScheme === 'dark';
    const gradient = theme.other.gradients[gradientVariant];

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    return (
      <Stack ref={ref} gap={0}>
        {items.map((item, index) => {
          const isActive = activeIndex !== undefined && index <= activeIndex;
          const isCurrent = item.isCurrent || (activeIndex !== undefined && index === activeIndex);
          const isLast = index === items.length - 1;
          const currentBulletSize = isCurrent ? bulletSize + 8 : bulletSize;

          return (
            <Box
              key={index}
              style={{
                display: 'flex',
                gap: 16,
                paddingBottom: isLast ? 0 : 24,
              }}
            >
              {/* Bullet and line */}
              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: bulletSize + 8, // Reserve space for current bullet
                }}
              >
                {/* Bullet */}
                <Box
                  style={{
                    width: currentBulletSize,
                    height: currentBulletSize,
                    borderRadius: '50%',
                    background:
                      gradientLine && isActive
                        ? `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`
                        : item.color || (isActive ? gradient.from : isDark ? theme.colors.dark[4] : theme.colors.gray[4]),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: isCurrent ? `0 0 0 4px ${item.color || gradient.from}33, 0 0 12px ${item.color || gradient.from}66` : undefined,
                    transition: 'all 0.2s ease',
                  }}
                >
                  {item.icon && (
                    <Box style={{ color: 'white', display: 'flex' }}>
                      {item.icon}
                    </Box>
                  )}
                </Box>

                {/* Line */}
                {!isLast && (
                  <Box
                    style={{
                      flex: 1,
                      width: lineWidth,
                      minHeight: 24,
                      background:
                        gradientLine && isActive
                          ? `linear-gradient(180deg, ${gradient.from}, ${gradient.to})`
                          : isDark
                          ? theme.colors.dark[4]
                          : theme.colors.gray[3],
                      marginTop: 8,
                    }}
                  />
                )}
              </Box>

              {/* Content */}
              <Box style={{ flex: 1, paddingTop: isCurrent ? 4 : 0 }}>
                <Text
                  fw={isCurrent ? 700 : 600}
                  size={isCurrent ? 'md' : 'sm'}
                  style={
                    gradientLine && isActive
                      ? {
                          backgroundImage: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          ...accessibilityStyles,
                        }
                      : isCurrent
                      ? { color: item.color, ...accessibilityStyles }
                      : accessibilityStyles
                  }
                >
                  {item.title}
                  {isCurrent && ' ← Current'}
                </Text>
                {item.date && (
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>
                    {new Intl.DateTimeFormat(locale || 'en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      timeZone: timezone,
                    }).format(new Date(item.date))}
                  </Text>
                )}
                {item.description && (
                  <Text size="sm" c={isCurrent ? undefined : 'dimmed'} mt={4} fw={isCurrent ? 500 : 400} style={accessibilityStyles}>
                    {item.description}
                  </Text>
                )}
              </Box>
            </Box>
          );
        })}
      </Stack>
    );
  }
);

Timeline.displayName = 'Timeline';

export default Timeline;
